import {
  HEX_IMAGE_HEIGHT,
  HEX_IMAGE_WIDTH,
  HEX_SIZE,
  type MapBounds,
} from "@west-marches/shared";

export const MAP_ZOOM_LEVELS = [0.55, 0.7, 0.9] as const;

// How far the viewport edge must stay inside the map's outer hex ring,
// expressed as a fraction of the map's outer extent. 1/3 means panning
// stops once the viewport edge is one third of the way in from the edge,
// so the void beyond the outermost water hexes is never visible.
export const CAMERA_EDGE_INSET = 1 / 3;

export function getMapWorldBounds(bounds: MapBounds): { xMax: number; yMax: number } {
  // Flat-top axial in odd-q offset:
  //   x of column q = HEX_SIZE * 1.5 * q
  //   y of (q,r)    = HEX_SIZE * sqrt(3) * (r + q/2)
  // Add half a tile to reach the visual edge of the outermost hex artwork.
  // Odd columns extend half a row farther in y than even columns, so include
  // that 0.5 row slack in the vertical extent.
  const xMax = HEX_SIZE * 1.5 * bounds.halfCols + HEX_IMAGE_WIDTH / 2;
  const yMax = HEX_SIZE * Math.sqrt(3) * (bounds.halfRows + 0.5) + HEX_IMAGE_HEIGHT / 2;
  return { xMax, yMax };
}

export function clampCamera(
  camera: { x: number; y: number; zoom: number },
  canvas: { width: number; height: number },
  bounds: MapBounds,
): void {
  const { xMax, yMax } = getMapWorldBounds(bounds);
  const allowedX = xMax * (1 - CAMERA_EDGE_INSET);
  const allowedY = yMax * (1 - CAMERA_EDGE_INSET);

  // Viewport spans world-x from -(canvas.width/2 + cam.x)/zoom to
  // (canvas.width/2 - cam.x)/zoom. Clamp cam.x so neither edge escapes
  // [-allowedX, allowedX]. Same for y.
  const minX = canvas.width / 2 - allowedX * camera.zoom;
  const maxX = allowedX * camera.zoom - canvas.width / 2;
  if (minX > maxX) {
    // Viewport is wider than the allowed area; only one valid position: centered.
    camera.x = 0;
  } else if (camera.x < minX) {
    camera.x = minX;
  } else if (camera.x > maxX) {
    camera.x = maxX;
  }

  const minY = canvas.height / 2 - allowedY * camera.zoom;
  const maxY = allowedY * camera.zoom - canvas.height / 2;
  if (minY > maxY) {
    camera.y = 0;
  } else if (camera.y < minY) {
    camera.y = minY;
  } else if (camera.y > maxY) {
    camera.y = maxY;
  }
}

export function screenToWorld(
  canvas: HTMLCanvasElement,
  camera: { x: number; y: number; zoom: number },
  sx: number,
  sy: number,
) {
  const wx = (sx - canvas.width / 2 - camera.x) / camera.zoom;
  const wy = (sy - canvas.height / 2 - camera.y) / camera.zoom;
  return { wx, wy };
}

export function getClosestZoomLevelIndex(zoom: number): number {
  let closestIndex = 0;
  let closestDistance = Number.POSITIVE_INFINITY;

  for (let index = 0; index < MAP_ZOOM_LEVELS.length; index += 1) {
    const distance = Math.abs(MAP_ZOOM_LEVELS[index] - zoom);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = index;
    }
  }

  return closestIndex;
}

export function clampZoomLevelIndex(index: number): number {
  return Math.max(0, Math.min(index, MAP_ZOOM_LEVELS.length - 1));
}

export function applyZoomAtScreenPoint(
  canvas: HTMLCanvasElement,
  camera: { x: number; y: number; zoom: number },
  nextZoom: number,
  sx: number,
  sy: number,
) {
  const { wx, wy } = screenToWorld(canvas, camera, sx, sy);
  camera.zoom = nextZoom;
  camera.x = sx - canvas.width / 2 - wx * nextZoom;
  camera.y = sy - canvas.height / 2 - wy * nextZoom;
}
